<template>
  <v-card>
    <v-data-table
      item-key="id"
      :page="BillableCurrentPage"
      :headers="headers"
      :items="BillableEnquiries"
      :items-per-page="itemsPerPage"
      :server-items-length="BillableTotal"
      :loading="BillableEnquiriesLoading"
      :footer-props="{ 'items-per-page-options': rowsPerPageItems }"
      :sort-by="BillableSortBy"
      :sort-desc="BillableSortDesc"
      :must-sort="true"
      single-expand
      show-expand
      @update:page="updatePage"
      @update:options="updateOptions"
    >
      <template v-slot:top>
        <v-toolbar flat color="white">
          <v-toolbar-title>Filters</v-toolbar-title>
          <v-divider class="mx-4" inset vertical></v-divider>
          <v-text-field
            name="search"
            v-model="search"
            prepend-inner-icon="search"
            clearable
            flat
            label="Search deals"
            hide-details
            autocomplete="off"
            class="mx-2"
          ></v-text-field>
          <v-select label="Status" :items="statuses" v-model="status" hide-details class="ml-2"></v-select>
          <v-divider class="mx-4" inset vertical></v-divider>
          <v-select
            hide-details
            :items="dateFields"
            item-text="name"
            item-value="id"
            v-model="dateColumn"
            label="Date Field"
            dense
            class="mr-2"
          ></v-select>
          <v-menu
            ref="fromMenu"
            v-model="showStartDatePicker"
            :close-on-content-click="false"
            transition="scale-transition"
            full-width
            offset-y
            :return-value.sync="startDate"
            class="xs6"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                label="Start date"
                v-model="prettyStartDate"
                prepend-inner-icon="event"
                readonly
                hide-details
                v-on="on"
                class="mx-2"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="startDate"
              no-title
              scrollable
              min="2016-05-01"
              :max="new Date().toISOString().substr(0, 10)"
              @change="$refs.fromMenu.save(startDate)"
            >
              <v-spacer></v-spacer>
              <v-btn text color="primary" @click="showStartDatePicker = false">Cancel</v-btn>
            </v-date-picker>
          </v-menu>
          <v-menu
            ref="toMenu"
            v-model="showEndDatePicker"
            :close-on-content-click="false"
            transition="scale-transition"
            full-width
            offset-y
            :return-value.sync="endDate"
            class="xs6"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                label="End date"
                v-model="prettyEndDate"
                prepend-inner-icon="event"
                readonly
                hide-details
                v-on="on"
                class="mx-2"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="endDate"
              no-title
              scrollable
              :min="startDate"
              :max="new Date().toISOString().substr(0, 10)"
              @change="$refs.toMenu.save(endDate)"
            >
              <v-spacer></v-spacer>
              <v-btn text color="primary" @click="showEndDatePicker = false">
                Cancel
              </v-btn>
            </v-date-picker>
          </v-menu>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn class="ma-2" tile large icon v-on="on" @click="showDownload = true">
                <v-icon>fas fa-file-download</v-icon>
              </v-btn>
              <BillExportDialog :show.sync="showDownload" />
            </template>
            <span>Download all bills as CSV</span>
          </v-tooltip>
        </v-toolbar>
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="py-4">
          <v-simple-table class="mb-2">
            <tbody>
              <tr>
                <td>
                  <strong>Customer Contact Name:</strong> {{ item.customer.contact_name.platform }}<br />
                  <strong>Provider CFX ENT:</strong> {{ item.product.provider.job_code }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
          <bill-list-table :bills="item.bills" @edit="editBill" />
        </td>
      </template>
      <template v-slot:item.customer.name.platform="{ item }">
        {{ item.customer.name.platform }}
        <v-icon color="grey lighten-1" small v-if="hasComments(item.bills)" class="ml-2">
          fa-comments
        </v-icon>
      </template>
      <template v-slot:item.funded_by="{ item }">
        {{
          item.product.registered_company_name ||
            item.product.provider.registered_company_name ||
            item.product.provider.name
        }}
      </template>
      <template v-slot:item.funding_obtained="{ item }">
        {{ item.funding_obtained | currency('£', ',', 2) }}
      </template>
      <template v-slot:item.created_at="{ item }">
        {{ item.created_at | dateOrPlaceholder }}
      </template>
      <template v-slot:item.bills="{ item }">
        {{ item.bills ? item.bills.length : 0 }}
      </template>
      <template v-slot:item.action="{ item }">
        <v-icon title="Add new bill" @click="addBill(item)">
          add
        </v-icon>
      </template>
    </v-data-table>
    <BillAddPopupDialog :dialog="showBillAdd" :enquiry="selectedEnquiry" @changed="showBillAdd = false" />
    <BillEditDialog
      :dialog="showBillEdit"
      :bill-details="selectedBill"
      @updated="showBillEdit = false"
    ></BillEditDialog>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import _ from 'lodash';
import moment from 'moment';
import { DateTime } from 'luxon';
import BillEditDialog from '@/components/Platform/Bills/BillEditDialog';
import BillAddPopupDialog from '@/components/Platform/Bills/BillAddPopupDialog';
import BillExportDialog from '@/components/Platform/Bills/BillExportDialog';
import BillListTable from '@/components/Platform/Bills/BillListTable';

export default {
  name: 'BillTableView',
  components: { BillListTable, BillExportDialog, BillAddPopupDialog, BillEditDialog },
  data: () => ({
    selectedBill: null,
    selectedEnquiry: null,
    showBillAdd: false,
    showBillEdit: false,
    showDownload: false,
    showStartDatePicker: false,
    showEndDatePicker: false,
    statuses: ['new revolving', 'new', 'billed', 'received'],
    dateFields: [
      { id: 'created_at', name: 'Date created' },
      { id: 'date_funding_obtained', name: 'Date funded' }
    ],
    filterState: {
      dateColumn: 'date_funding_obtained',
      search: null,
      status: 'new'
    },
    headers: [
      { text: 'Company Name', value: 'customer.name.platform', sortable: false },
      { text: 'Amount Funded', value: 'funding_obtained' },
      { text: 'Funded By', value: 'funded_by', sortable: false },
      { text: 'Date Submitted', value: 'created_at' },
      { text: 'Date Funded', value: 'date_funding_obtained' },
      { text: 'Referred By', value: 'customer.referrer.name', sortable: false },
      { text: 'Bills', value: 'bills', sortable: false },
      { text: 'Actions', value: 'action', sortable: false }
    ],
    rowsPerPageItems: [15, 25, 50]
  }),
  computed: {
    ...mapGetters([
      'BillableEnquiries',
      'BillableEnquiriesLoading',
      'BillableFilters',
      'BillableSearch',
      'BillableCurrentPage',
      'BillablePageSize',
      'BillableLastPage',
      'BillableTotal',
      'BillableSortBy',
      'BillableSortDesc'
    ]),
    itemsPerPage() {
      return this.BillablePageSize;
    },
    search: {
      get() {
        return this.BillableSearch;
      },
      set: _.debounce(function(value) {
        this.setBillableEnquirySearch(value).then(() => this.getBillableEnquiries(true));
      }, 350)
    },
    status: {
      get() {
        return this.BillableFilters.status;
      },
      set(value) {
        this.setBillableEnquiryStatus(value);
      }
    },
    startDate: {
      get() {
        return this.BillableFilters.dates.from;
      },
      set(value) {
        if (value !== this.BillableFilters.dates.from) {
          this.setBillableEnquiryFromDate(value).then(() => {
            this.getBillableEnquiries(true);
          });
        }
      }
    },
    endDate: {
      get() {
        return this.BillableFilters.dates.to;
      },
      set(value) {
        if (value !== this.BillableFilters.dates.to) {
          this.setBillableEnquiryToDate(value).then(() => {
            this.getBillableEnquiries(false);
          });
        }
      }
    },
    dateColumn: {
      get() {
        return this.BillableFilters.dates.column;
      },
      set(value) {
        this.setBillableEnquiryDateColumn(value);
      }
    },
    prettyStartDate() {
      return _.isNull(this.BillableFilters.dates.from)
        ? null
        : DateTime.fromISO(this.BillableFilters.dates.from).toLocaleString();
    },
    prettyEndDate() {
      return _.isNull(this.BillableFilters.dates.to)
        ? null
        : DateTime.fromISO(this.BillableFilters.dates.to).toLocaleString();
    }
  },
  methods: {
    ...mapActions([
      'getBillableEnquiries',
      'setBillableEnquiryCurrentPage',
      'setBillableEnquiryPageSize',
      'setBillableEnquiryFromDate',
      'setBillableEnquiryToDate',
      'setBillableEnquiryDateColumn',
      'setBillableEnquirySearch',
      'setBillableEnquiryStatus',
      'setBillableEnquirySortOrder',
      'toggleLeftDrawer'
    ]),
    addBill(enquiry) {
      this.selectedEnquiry = enquiry;
      this.showBillAdd = true;
    },
    editBill(bill) {
      this.selectedBill = bill;
      this.showBillEdit = true;
    },
    hasComments(bills) {
      return bills && bills.some(bill => bill.comments);
    },
    updateOptions(options) {
      if (this.itemsPerPage !== options.itemsPerPage) {
        this.setBillableEnquiryPageSize(options.itemsPerPage);
      }
      if (this.BillableSortBy !== options.sortBy[0] || this.BillableSortDesc !== options.sortDesc[0]) {
        const direction = options.sortDesc[0] ? 'desc' : 'asc';
        this.setBillableEnquirySortOrder({
          orderBy: options.sortBy[0] || 'date_funding_obtained',
          orderDirection: !options.sortBy[0] ? 'desc' : direction
        });
      }
    },
    updatePage(value) {
      this.setBillableEnquiryCurrentPage(value);
    }
  },
  filters: {
    dateOrPlaceholder(date, fallbackText = '') {
      if (date) {
        return moment(date).format('YYYY-MM-DD');
      }
      return fallbackText;
    }
  },
  created() {
    this.getBillableEnquiries();
  }
};
</script>

<style lang="scss" scoped></style>
